// src/journey/PlanningProgress.tsx
//
// BekalNanti — P1 Planning Journey Progress Model
// Renders the true planning stages without false completion.

import React, { useState } from 'react'
import { PlanningStage } from './types'
import { Check, Clock, ChevronDown, ChevronUp, CircleDot } from 'lucide-react'

interface PlanningProgressProps {
  stages: PlanningStage[]
}

export const PlanningProgress: React.FC<PlanningProgressProps> = ({ stages }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const activeStage =
    stages.find((s) => s.status === 'in_progress') ||
    stages[0] || {
      id: 'goal' as const,
      number: 1,
      title: 'Tujuan Masa Depan',
      subtitle: '',
      status: 'not_started' as const,
      statusLabel: 'Belum dimulai',
    }
  const completedCount = stages.filter((s) => s.status === 'completed').length

  const getStatusBadge = (status: PlanningStage['status'], label: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
            <Check size={11} strokeWidth={2.6} />
            <span>{label}</span>
          </span>
        )
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-[#0F766E] border border-teal-200 text-[10px] font-bold animate-pulse">
            <CircleDot size={11} className="text-[#0EA5A4]" />
            <span>{label}</span>
          </span>
        )
      case 'waiting_data':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium">
            <Clock size={11} />
            <span>{label}</span>
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-medium">
            <span>{label}</span>
          </span>
        )
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm text-left space-y-3">
      {/* Header & Summary */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
            Tahap Pemetaan Rencana
          </span>
          <h3 className="text-sm font-bold text-slate-900 mt-0.5">
            Tahap {activeStage.number} dari {stages.length}: {activeStage.title}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-1 text-xs cursor-pointer"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Sembunyikan detail tahap' : 'Lihat semua tahap pemetaan'}
        >
          <span className="text-[11px] font-medium hidden sm:inline">
            {isExpanded ? 'Ringkas' : 'Lihat Semua'}
          </span>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Visual Stepper / Progress Bar */}
      <div
        className="grid gap-1.5 pt-1"
        style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}
      >
        {stages.map((stage) => {
          let barBg = 'bg-slate-200'
          if (stage.status === 'completed') barBg = 'bg-emerald-500'
          else if (stage.status === 'in_progress') barBg = 'bg-[#0EA5A4]'

          return (
            <div key={stage.id} className="space-y-1">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${barBg}`}
                title={`Tahap ${stage.number}: ${stage.title} (${stage.statusLabel})`}
              />
            </div>
          )
        })}
      </div>

      {/* Progress Helper Note */}
      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-0.5">
        <span className="font-medium text-slate-600">
          {completedCount} tahap selesai • {stages.length - completedCount} tahap berikutnya
        </span>
        <span className="text-slate-400 italic">Progress nyata, tanpa angka palsu</span>
      </div>

      {/* Expanded Stages List */}
      {isExpanded && (
        <div className="pt-2 border-t border-slate-100 space-y-2.5 animate-in fade-in-50 duration-200">
          {stages.map((stage) => {
            const isCurrent = stage.status === 'in_progress'

            return (
              <div
                key={stage.id}
                className={`p-2.5 rounded-xl border text-xs flex items-start justify-between gap-3 transition-colors ${
                  isCurrent
                    ? 'border-teal-200 bg-teal-50/40'
                    : stage.status === 'completed'
                    ? 'border-slate-200 bg-white'
                    : 'border-slate-100 bg-slate-50/60 opacity-80'
                }`}
              >
                <div className="flex items-start gap-2.5 min-w-0">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5 ${
                      stage.status === 'completed'
                        ? 'bg-emerald-100 text-emerald-700'
                        : isCurrent
                        ? 'bg-[#0EA5A4] text-white'
                        : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {stage.status === 'completed' ? <Check size={11} strokeWidth={3} /> : stage.number}
                  </div>
                  <div className="min-w-0">
                    <p
                      className={`font-semibold leading-snug ${
                        isCurrent ? 'text-[#0F766E]' : 'text-slate-800'
                      }`}
                    >
                      {stage.title}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{stage.subtitle}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 pt-0.5">
                  {getStatusBadge(stage.status, stage.statusLabel)}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
